import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../../../services/AxiosInstance';
import Api from '../../../../../constants/Api';
import { Button } from 'react-bootstrap';
import { successAlert, errorAlert } from '../../../alert/defaultAlert';
import LoadingIcon from '../../../Icon/LoadingIcon';
import { buildWhatsappUrl } from '../../../../../utils/FormatUtils';
import OutsidePaymentAction from '../../Charge/OutsidePaymentAction';
import './CustomerLatePaymentAlert.css';
import '../../Charge/ChargeActionButton.css';

/**
 * Aviso de mensalidade em atraso, com as faturas vencidas e as ações de cobrança de cada uma.
 *
 * @param {object} customer cliente com clubLate e, para abrir caixa na baixa por fora, a filial
 * @param {function} onRegularized chamado quando uma baixa por fora deixa o cliente sem nenhuma
 *        fatura vencida, para a tela tirar a marca de atraso (ícone, aviso ao fechar a venda)
 */
const CustomerLatePaymentAlert = ({ customer, onRegularized }) => {
    const [overdueCharges, setOverdueCharges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generatingId, setGeneratingId] = useState(null);
    // Fechar vale para a visita atual, não fica gravado: ao abrir outro cliente, ou o
    // mesmo de novo, o débito volta a aparecer. Esconder para sempre um aviso de
    // dinheiro em aberto seria pior que incomodar.
    const [dismissed, setDismissed] = useState(false);
    // Cliente que pagou no balcão e ficou em dia nesta visita: o aviso sai mesmo antes de a
    // tela recarregar o clubLate.
    const [regularized, setRegularized] = useState(false);

    /**
     * @param {function} onLoaded opcional: recebe a lista recém-carregada
     */
    const fetchOverdueCharges = (onLoaded) => {
        if (customer?.id && customer?.clubLate) {
            setLoading(true);
            axiosInstance.get(`${Api.subscription.charge}/overdue/customer/${customer.id}`)
                .then(resp => {
                    const charges = resp.data || [];
                    setOverdueCharges(charges);
                    onLoaded?.(charges);
                })
                .catch(err => {
                    console.error("Erro ao buscar cobranças vencidas", err);
                })
                .finally(() => setLoading(false));
        }
    };

    useEffect(() => {
        setDismissed(false);
        setRegularized(false);
        fetchOverdueCharges();
    }, [customer?.id, customer?.clubLate]);

    // Quem manda no aviso é o clubLate, não a lista de faturas: antes, se a consulta das
    // cobranças voltasse vazia, o alerta sumia e quem atendia não ficava sabendo da
    // inadimplência. Sem as faturas o alerta perde as ações de cobrança, mas o aviso fica.
    // A exceção é a baixa por fora feita aqui mesmo, que confirma pela consulta que não sobrou
    // nada vencido (regularized).
    if (!customer?.clubLate || dismissed || regularized) {
        return null;
    }

    // Depois da baixa por fora, recarrega as faturas. Sem nenhuma vencida, o cliente está em dia:
    // é a mesma consulta que decide o clubLate no servidor (faturas em aberto já vencidas).
    const handleOutsidePaymentRegistered = () => {
        fetchOverdueCharges((charges) => {
            if (charges.length === 0) {
                setRegularized(true);
                onRegularized?.();
            }
        });
    };

    const handleCopyLink = (link) => {
        navigator.clipboard.writeText(link)
            .then(() => {
                successAlert("Link de pagamento copiado com sucesso!");
            });
    };

    const handleSendWhatsApp = (link, phone) => {
        const msg = `Olá! Segue o link de pagamento para regularizar sua assinatura do clube: ${link}`;
        window.open(buildWhatsappUrl(phone, msg), '_blank');
    };

    const handleCopyPix = (pixKey) => {
        navigator.clipboard.writeText(pixKey)
            .then(() => {
                successAlert("Código Pix Copia e Cola copiado com sucesso!");
            });
    };

    const handleGenerateLink = (chargeId) => {
        setGeneratingId(chargeId);
        axiosInstance.post(`${Api.subscription.charge}/${chargeId}/payment-link`)
            .then(resp => {
                successAlert("Link de pagamento gerado com sucesso!");
                fetchOverdueCharges();
            })
            .catch(err => {
                errorAlert(err);
            })
            .finally(() => {
                setGeneratingId(null);
            });
    };

    // Soma o que está em aberto: com mais de uma fatura, quem atende precisava somar de cabeça.
    const totalOverdue = overdueCharges.reduce((total, charge) => total + Number(charge.amount || 0), 0);

    // Dias de atraso comunicam gravidade melhor que a data pura: "há 70 dias" pesa mais
    // que "22/05". Comparação por dia cheio, ignorando a hora.
    const daysOverdue = (dateString) => {
        if (!dateString) return null;

        const dueDate = new Date(dateString);
        if (Number.isNaN(dueDate.getTime())) return null;

        const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const diff = startOfDay(new Date()) - startOfDay(dueDate);
        const days = Math.floor(diff / 86400000);

        return days > 0 ? days : null;
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return 'R$ 0,00';
        return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR');
        } catch (e) {
            return dateString;
        }
    };

    return (
        <div className="customer-late-alert" role="alert">
            <div className="d-flex align-items-start mb-2">
                <i className="fas fa-exclamation-triangle fa-lg me-3 mt-1 customer-late-alert__icon"></i>
                <div className="flex-grow-1">
                    <h5 className="customer-late-alert__title">
                        Mensalidade em atraso
                    </h5>
                    <div className="customer-late-alert__text">
                        {overdueCharges.length > 0 ? (
                            <>
                                {overdueCharges.length === 1
                                    ? '1 fatura em aberto, no total de '
                                    : `${overdueCharges.length} faturas em aberto, somando `}
                                <strong>{formatCurrency(totalOverdue)}</strong>
                                {customer.clubPlanName ? <> no plano <strong>{customer.clubPlanName}</strong></> : ' na assinatura do Clube'}.
                                {' '}Regularize antes de liberar o benefício.
                            </>
                        ) : loading ? (
                            'Buscando as faturas em aberto deste cliente.'
                        ) : (
                            // Sem a lista não dá para dizer valor nem vencimento, então o texto se
                            // limita ao que é certo: a assinatura está em atraso.
                            <>
                                Este cliente está com a assinatura
                                {customer.clubPlanName ? <> do plano <strong>{customer.clubPlanName}</strong></> : ' do Clube'}
                                {' '}em atraso. Confira as faturas em Assinaturas antes de liberar o benefício.
                            </>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    className="btn-close ms-3"
                    aria-label="Fechar aviso"
                    title="Fechar aviso"
                    onClick={() => setDismissed(true)}
                ></button>
            </div>

            {loading && overdueCharges.length === 0 && (
                <div className="text-center py-2 customer-late-alert__loading">
                    <LoadingIcon className="me-2" /> Carregando faturas...
                </div>
            )}

            {overdueCharges.length > 0 && (
                <div className="mt-3 pt-2 customer-late-alert__charges">
                    {overdueCharges.map((charge, idx) => (
                        <div key={charge.id || idx} className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2 customer-late-alert__charge">
                            <div className="d-flex align-items-center">
                                {/* O bloco inteiro já é sobre faturas vencidas: repetir "Vencida" em
                                    cada linha ocupava espaço sem dizer nada de novo. */}
                                <span className="customer-late-alert__charge-line">
                                    Venceu em <strong>{formatDate(charge.dueDate)}</strong>
                                    {daysOverdue(charge.dueDate) && (
                                        <span className="customer-late-alert__charge-days">
                                            {' '}· há {daysOverdue(charge.dueDate)} dia{daysOverdue(charge.dueDate) > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {' '}· <strong>{formatCurrency(charge.amount)}</strong>
                                </span>
                            </div>
                            {/* Mesmos botões de fundo suave das ações da cobrança
                                (ChargeActionButton.css), para o balcão ver o mesmo visual da tela
                                de Cobranças; o WhatsApp segue com a cor própria. */}
                            <div className="d-flex gap-2 flex-wrap align-items-center">
                                {charge.paymentLink ? (
                                    <>
                                        <Button
                                            variant=""
                                            size="sm"
                                            className="charge-action-btn charge-action-btn--link"
                                            onClick={() => handleCopyLink(charge.paymentLink)}
                                        >
                                            <i className="fas fa-copy me-1"></i> Copiar Link
                                        </Button>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={() => handleSendWhatsApp(charge.paymentLink, customer.phone)}
                                            style={{ fontWeight: '600' }}
                                        >
                                            <i className="fab fa-whatsapp me-1"></i> WhatsApp
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant=""
                                            size="sm"
                                            className="charge-action-btn charge-action-btn--link"
                                            disabled={generatingId === charge.id}
                                            onClick={() => handleGenerateLink(charge.id)}
                                        >
                                            {generatingId === charge.id ? (
                                                <>
                                                    <LoadingIcon className="me-1" /> Gerando Link...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-link me-1"></i> Gerar Link de Pagamento
                                                </>
                                            )}
                                        </Button>
                                        {charge.pixKey && (
                                            <Button
                                                variant=""
                                                size="sm"
                                                className="charge-action-btn charge-action-btn--pix"
                                                onClick={() => handleCopyPix(charge.pixKey)}
                                            >
                                                <i className="fas fa-qrcode me-1"></i> Copiar Pix
                                            </Button>
                                        )}
                                    </>
                                )}
                                {/* O cliente em atraso está no balcão: pagando ali mesmo, a baixa
                                    lança no caixa de quem atende e avisa o provedor. */}
                                <OutsidePaymentAction
                                    charge={charge}
                                    branchId={customer.branch?.id}
                                    compact
                                    onRegistered={handleOutsidePaymentRegistered}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CustomerLatePaymentAlert;
